import { useMemo } from "react";
import { Field, TextInput, SelectField, ChoiceGrid } from "./FormFields";
import SearchableSelect from "@/components/suffa/SearchableSelect";
import { calculateAgeParts } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";
import {
  GENDER_OPTIONS,
  CHILDREN_OPTIONS,
  SCHOOL_EDUCATION_OPTIONS,
  MADARSA_EDUCATION_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  COMMUNITY_JAMAAT_OPTIONS,
  FORM_PROFESSIONS,
} from "@/lib/profileForm";
import {
  getCountryNames,
  getStateNames,
  getCityNames,
  getDivisionLabel,
  NATIONALITIES,
} from "@/lib/locations";

const pad = (n) => String(n).padStart(2, "0");

export const StepProfileFor = ({ data, set, errors }) => (
  <div className="cp-grid cp-grid-1">
    <Field label="Who is this profile for?" required error={errors.profileFor}>
      <ChoiceGrid
        options={["Myself", "My Son", "My Daughter", "My Brother", "My Sister", "Family Member"]}
        value={data.profileFor}
        onChange={(v) => set("profileFor", v)}
        name="Profile for"
        testIdPrefix="cp-for"
      />
    </Field>
  </div>
);

const dobString = (dob) =>
  dob && dob.year && dob.month && dob.day ? `${dob.year}-${pad(dob.month)}-${pad(dob.day)}` : "";

export const StepBasic = ({ data, set, errors }) => {
  const { t } = useLanguage();
  const age = calculateAgeParts(data.dob);
  const divisionLabel = getDivisionLabel(data.country);
  const stateNames = useMemo(() => getStateNames(data.country), [data.country]);
  const stateListEmpty = data.country && stateNames.length === 0;
  const cityNames = useMemo(
    () => getCityNames(data.country, data.state),
    [data.country, data.state],
  );

  const setCountry = (name) => {
    set("country", name);
    set("state", "");
    set("city", "");
  };

  const setState = (name) => {
    set("state", name);
    set("city", "");
  };

  return (
    <div className="cp-grid">
      <TextInput
        label="Full Name"
        required
        id="cp-fullname"
        placeholder="Full name"
        autoComplete="name"
        value={data.fullName}
        onChange={(e) => set("fullName", e.target.value)}
        error={errors.fullName}
      />
      <SelectField
        label="Gender"
        required
        id="cp-gender"
        options={GENDER_OPTIONS}
        value={data.gender}
        onChange={(v) => set("gender", v)}
        error={errors.gender}
      />
      <Field
        label="Date of Birth"
        required
        error={errors.dob}
        hint="Age is calculated automatically from the date of birth."
      >
        <input
          type="date"
          className={`cp-input${errors.dob ? " has-error" : ""}`}
          max={new Date().toISOString().slice(0, 10)}
          min="1930-01-01"
          value={dobString(data.dob)}
          data-testid="cp-dob"
          onChange={(e) => {
            const [y, m, d] = e.target.value
              ? e.target.value.split("-").map(Number)
              : ["", "", ""];
            set(
              "dob",
              e.target.value ? { day: d, month: m, year: y } : { day: "", month: "", year: "" },
            );
          }}
        />
        {age && (
          <p className="cp-age" data-testid="cp-age-display">
            Age: {age.years} years {age.months} months
          </p>
        )}
      </Field>
      <Field label="Country" required error={errors.country}>
        <SearchableSelect
          options={getCountryNames()}
          value={data.country}
          onChange={setCountry}
          placeholder="Select Country"
          testId="cp-country"
        />
      </Field>
      <Field label={divisionLabel} required error={errors.state}>
        {!data.country ? (
          <input
            disabled
            className="cp-input"
            placeholder={t("Select a country first")}
            data-testid="cp-state-disabled"
          />
        ) : stateListEmpty ? (
          <input
            className={`cp-input${errors.state ? " has-error" : ""}`}
            placeholder={`Enter ${divisionLabel.toLowerCase()}`}
            value={data.state}
            data-testid="cp-state"
            onChange={(e) => set("state", e.target.value)}
          />
        ) : (
          <SearchableSelect
            options={stateNames}
            value={data.state}
            onChange={setState}
            placeholder={`Select ${divisionLabel}`}
            testId="cp-state"
          />
        )}
      </Field>
      <Field label="City" required error={errors.city}>
        {!data.state ? (
          <input
            disabled
            className="cp-input"
            placeholder={`Select a ${divisionLabel.toLowerCase()} first`}
            data-testid="cp-city-disabled"
          />
        ) : cityNames.length ? (
          <SearchableSelect
            options={cityNames}
            value={data.city}
            onChange={(v) => set("city", v)}
            placeholder="Select City"
            testId="cp-city"
          />
        ) : (
          <input
            className={`cp-input${errors.city ? " has-error" : ""}`}
            placeholder={t("Enter city")}
            value={data.city}
            data-testid="cp-city"
            onChange={(e) => set("city", e.target.value)}
          />
        )}
      </Field>
      <Field label="Nationality" required error={errors.nationality}>
        <SearchableSelect
          options={NATIONALITIES}
          value={data.nationality}
          onChange={(v) => set("nationality", v)}
          placeholder="Select Nationality"
          testId="cp-nationality"
        />
      </Field>
      <Field label="Community / Jamaat" required error={errors.communityJamaat}>
        <SearchableSelect
          options={
            data.communityJamaat && !COMMUNITY_JAMAAT_OPTIONS.includes(data.communityJamaat)
              ? [...COMMUNITY_JAMAAT_OPTIONS, data.communityJamaat]
              : COMMUNITY_JAMAAT_OPTIONS
          }
          value={data.communityJamaat}
          onChange={(v) => set("communityJamaat", v)}
          placeholder="Select Community / Jamaat"
          testId="cp-community"
        />
      </Field>
      {data.communityJamaat === "Other" && (
        <Field label="Enter your Community / Jamaat" required error={errors.communityJamaatOther}>
          <input
            className={`cp-input${errors.communityJamaatOther ? " has-error" : ""}`}
            placeholder="e.g. Dewan, Cutchi Memon"
            value={data.communityJamaatOther}
            data-testid="cp-community-other"
            onChange={(e) => set("communityJamaatOther", e.target.value)}
          />
        </Field>
      )}
      <SelectField
        label="Marital Status"
        required
        id="cp-marital"
        options={MARITAL_STATUS_OPTIONS}
        value={data.maritalStatus}
        onChange={(v) => set("maritalStatus", v)}
        error={errors.maritalStatus}
      />
      {data.maritalStatus && data.maritalStatus !== "Never Married" && (
        <SelectField
          label="Children"
          required
          id="cp-children"
          options={CHILDREN_OPTIONS}
          value={data.children}
          onChange={(v) => set("children", v)}
          error={errors.children}
        />
      )}
    </div>
  );
};

export const StepCareer = ({ data, set, errors }) => (
  <div className="cp-grid">
    <SelectField
      label="School Education"
      required
      id="cp-school-education"
      options={SCHOOL_EDUCATION_OPTIONS}
      value={data.schoolEducation}
      onChange={(v) => set("schoolEducation", v)}
      error={errors.schoolEducation}
    />
    {data.schoolEducation === "Other" && (
      <TextInput
        label="Enter Your School Education"
        required
        id="cp-school-education-other"
        placeholder="e.g. Senior Secondary, CBSE board"
        value={data.schoolEducationOther}
        onChange={(e) => set("schoolEducationOther", e.target.value)}
        error={errors.schoolEducationOther}
      />
    )}
    <SelectField
      label="Madarsa Education"
      required
      id="cp-madarsa-education"
      options={MADARSA_EDUCATION_OPTIONS}
      value={data.madarsaEducation}
      onChange={(v) => set("madarsaEducation", v)}
      error={errors.madarsaEducation}
    />
    {data.madarsaEducation === "Other" && (
      <TextInput
        label="Enter Your Madarsa Education"
        required
        id="cp-madarsa-education-other"
        placeholder="e.g. Aalim course, Lucknow"
        value={data.madarsaEducationOther}
        onChange={(e) => set("madarsaEducationOther", e.target.value)}
        error={errors.madarsaEducationOther}
      />
    )}
    <Field label="Profession" required error={errors.profession}>
      <SearchableSelect
        options={FORM_PROFESSIONS}
        pinnedOptions={["Other"]}
        value={data.profession}
        onChange={(v) => set("profession", v)}
        placeholder="Select Profession"
        testId="cp-profession"
      />
    </Field>
    {data.profession === "Other" && (
      <TextInput
        label="Enter Your Profession"
        required
        id="cp-profession-other"
        placeholder="e.g. Tailor, Driver, Counsellor"
        value={data.professionOther}
        onChange={(e) => set("professionOther", e.target.value)}
        error={errors.professionOther}
      />
    )}
    <TextInput
      label="Approx. Income"
      id="cp-income"
      placeholder="e.g. ₹10,00,000 per year"
      hint="Private — never shown publicly."
      value={data.income}
      onChange={(e) => set("income", e.target.value)}
      error={errors.income}
    />
  </div>
);
